import './media-panel.less';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, SliderInput, Tooltip } from '@meitu/saas-ui';
import { IMediaLayerProps, MediaType } from '@meitu/saas-editor-core/es/version-types';
import { BaseToolbar } from '@meitu/saas-editor-ui/es/components/base-toolbar';
import { DividerLine } from '@meitu/saas-editor-ui/es/components/divider-line';
import { HeaderToolbar } from '@meitu/saas-editor-ui/es/components/header-toolbar';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import { usePanelLayerId } from '@/hooks/use-panel-layer-id';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

const MediaOpacity = React.memo(({ id }: { id: string }) => {
    const { $t } = useLocaleStore();
    const originOpacity = useRef<number | null>(null);
    const opacity = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        return layer.opacity;
    });

    return (
        <div className="c-media-panel-opacity">
            <LabelAction title={$t((v) => v.panel.mediaPanel.opacity)}>
                <div className="text-action__item">
                    <SliderInput
                        value={parseInt(`${(opacity || 0) * 100}`)}
                        showStepBtn={true}
                        max={100}
                        min={0}
                        type="horizontal"
                        onChange={(val: number) => {
                            if (originOpacity.current === null) {
                                originOpacity.current = opacity;
                            }
                            editor.mediaLayer.setOpacity(id, val / 100);
                        }}
                        onAfterChange={(val: number) => {
                            editor.mediaLayer.setOpacity(id, val / 100);
                            originOpacity.current = null;
                        }}
                    />
                </div>
            </LabelAction>
        </div>
    );
});

const VideoUrlInput = ({ id }: { id: string }) => {
    const { $t } = useLocaleStore();
    const [videoUrl, setVideoUrl] = useState('');
    const [toolTip, setToolTip] = useState({ open: false, text: '' });
    const activeProps = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { data } = layer;
        const { url } = data as IMediaLayerProps;
        return { url };
    });

    useEffect(() => {
        if (toolTip.open) {
            setTimeout(() => {
                setToolTip((pre) => {
                    return {
                        ...pre,
                        open: false,
                    };
                });
            }, 2000);
        }
    }, [toolTip.open]);

    const updateVideoUrl = () => {
        if (!videoUrl) {
            return setToolTip({
                open: true,
                text: $t((v) => v.panel.mediaPanel.videoUrlInputError),
            });
        }
        if (videoUrl === activeProps?.url) return;
        editor.mediaLayer.setPosterUrl(id, { url: videoUrl });
    };
    return (
        <React.Fragment>
            <div className="c-media-panel-info">
                <Tooltip open={toolTip.open} title={toolTip.text}>
                    <Input
                        key="url"
                        value={videoUrl}
                        className={classNames('c-media-panel-info__input', {
                            'c-media-panel-info__input--warning': toolTip.open,
                        })}
                        placeholder={$t((v) => v.panel.mediaPanel.videoUrlPlaceholder)}
                        onChange={(e) => {
                            let { value } = e.target;
                            value = value.trimStart();
                            if (value === videoUrl) return;
                            setVideoUrl(value);
                        }}
                    />
                </Tooltip>
                <div className="c-media-panel-info__btns">
                    <Button key="url" type="primary" data-blue block onClick={updateVideoUrl}>
                        {$t((v) => v.panel.mediaPanel.updateVideoUrl)}
                    </Button>
                </div>
            </div>
            <DividerLine />
        </React.Fragment>
    );
};

const MediaPanel = () => {
    const id = usePanelLayerId();

    if (!id) return null;
    const layer = editor.stage.getLayer(id);
    const { mediaType } = layer?.data as IMediaLayerProps;
    return (
        <div className="c-media-panel">
            <HeaderToolbar id={id} editor={editor} style={{ padding: 16 }} />
            {mediaType === MediaType.Video ? <VideoUrlInput id={id} /> : null}
            <MediaOpacity id={id} />
            <DividerLine />
            <BaseToolbar id={id} editor={editor} style={{ padding: '0 16px 16px' }} />
        </div>
    );
};

export default React.memo(MediaPanel);
